import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Card,
  Field,
  Select,
  Stack,
  Title,
  useToast
} from "@introist/react-foundation/v2";
import styled from "styled-components";

import { TopBarRoute } from "components/templates";
import { SearchInput, Skeletons } from "components/molecules";
import { Empty } from "foundation";
import { useFeatureFlag } from "hooks";
import { useEmployees } from "modules/employees/hooks/useEmployees";
import { useWorkflows } from "modules/flows/hooks/useWorkflows";
import { createJourney } from "services/api/journeys/JourneyApi";
import {
  WellbeingScenarioType,
  buildWellbeingLaunchRequest,
  simulateWellbeingRows,
  generateHistoricalTrends,
  calculateAggregateStats
} from "../hooks/useWellbeingData";
import { useWellbeingPreferences } from "../hooks/useWellbeingPreferences";
import { WellbeingOverviewCards } from "../components/WellbeingOverviewCards";
import { EnhancedEmployeeTable } from "../components/EnhancedEmployeeTable";

const scenarioTypeTitles: Record<WellbeingScenarioType, string> = {
  PLUS_HOURS_HIGH: "Excess Overtime",
  MINUS_HOURS_LOW: "Hour Deficit",
  LONG_SICK_LEAVE: "Extended Sick Leave",
  SICK_LEAVES_12M_HIGH: "Frequent Sick Leaves",
  SHORT_SICK_LEAVES_3M_HIGH: "Recent Sick Leaves",
  VACATION_NOT_TAKEN: "Unused Vacation",
  BURNOUT_RISK_HIGH: "Burnout Risk",
  LOW_ENGAGEMENT: "Low Engagement",
  CONSECUTIVE_DAYS_HIGH: "No Time Off"
};

const ControlsCard = styled(Card)`
  .wellbeing-controls {
    display: grid;
    grid-template-columns: minmax(220px, 300px) minmax(220px, 260px) minmax(220px, 260px) 1fr;
    gap: var(--spacing-medium);
    align-items: end;
  }

  @media (max-width: 1100px) {
    .wellbeing-controls {
      grid-template-columns: 1fr;
    }
  }
`;


export const WellbeingRoute = () => {
  const toast = useToast();
  const hasWellbeingDashboard = useFeatureFlag("wellbeing-dashboard").isEnabled;
  const { employees } = useEmployees({ fields: ["name"] });
  const { workflows } = useWorkflows({});
  const { selectedWorkflowId, setSelectedWorkflowId } = useWellbeingPreferences();

  const [search, setSearch] = useState("");
  const [signalFilter, setSignalFilter] = useState<WellbeingScenarioType | "all">("all");
  const [severityFilter, setSeverityFilter] = useState<"all" | "medium" | "high">("all");
  const [launchingEmployeeId, setLaunchingEmployeeId] = useState<string | null>(null);

  useEffect(() => {
    if (!workflows || !selectedWorkflowId) return;
    if (!workflows.some(workflow => workflow.id === selectedWorkflowId)) {
      setSelectedWorkflowId(undefined);
    }
  }, [workflows, selectedWorkflowId, setSelectedWorkflowId]);

  const rows = useMemo(() => {
    return simulateWellbeingRows(employees ?? []);
  }, [employees]);

  const historicalTrends = useMemo(() => {
    return generateHistoricalTrends(employees ?? [], 30);
  }, [employees]);

  const aggregateStats = useMemo(() => {
    return calculateAggregateStats(employees ?? [], rows, historicalTrends);
  }, [employees, rows, historicalTrends]);

  const filteredRows = useMemo(() => {
    const lowerSearch = search.trim().toLowerCase();

    return rows.filter(row => {
      const matchSearch =
        lowerSearch.length === 0 || (row.employee.name ?? "").toLowerCase().includes(lowerSearch);
      const matchSignal = signalFilter === "all" || row.scenarios.some(s => s.type === signalFilter);
      const matchSeverity = severityFilter === "all" || row.topSeverity === severityFilter;

      return matchSearch && matchSignal && matchSeverity;
    });
  }, [rows, search, signalFilter, severityFilter]);

  const launchWorkflow = async (employeeId: string) => {
    if (!selectedWorkflowId) {
      toast.error("Select a workflow first");
      return;
    }

    setLaunchingEmployeeId(employeeId);
    try {
      const request = buildWellbeingLaunchRequest(employeeId, selectedWorkflowId);
      const response = await createJourney(request);
      toast.success(`Automation started (${response.id})`);
    } catch (error) {
      console.error("Failed to start automation:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      toast.error(`Failed to start automation: ${errorMessage}`);
    } finally {
      setLaunchingEmployeeId(null);
    }
  };

  const workflowOptions = (workflows ?? []).map(workflow => ({
    key: workflow.id,
    title: workflow.title
  }));

  if (!hasWellbeingDashboard) {
    return <Navigate to="/employees" replace />;
  }

  if (!employees || !workflows) {
    return (
      <TopBarRoute paths={[{ key: "wellbeing", title: "Employee Well-being" }]} medium>
        <Skeletons.List />
      </TopBarRoute>
    );
  }

  return (
    <TopBarRoute paths={[{ key: "wellbeing", title: "Employee Well-being Dashboard" }]} medium>
      <Stack vertical gap="xLarge">
        {/* KPI Overview Cards */}
        <WellbeingOverviewCards stats={aggregateStats} activeInterventions={0} showTeamWellbeing={false} />

        {/* Controls and Filters */}
        <ControlsCard>
          <Stack vertical gap="small">
            <Title variant="bold">Employee Wellbeing Signals</Title>
            <div className="wellbeing-controls">
              <Field title="Workflow to launch">
                <Select
                  searchable
                  placeholder="Select workflow"
                  style={{ width: "100%" }}
                  options={workflowOptions}
                  value={selectedWorkflowId}
                  onSelect={opt => setSelectedWorkflowId(opt.key)}
                />
              </Field>
              <Field title="Signal">
                <Select
                  style={{ width: "100%" }}
                  options={[
                    { key: "all", title: "All signals" },
                    ...Object.entries(scenarioTypeTitles).map(([key, title]) => ({ key, title }))
                  ]}
                  value={signalFilter}
                  onSelect={opt => setSignalFilter(opt.key as WellbeingScenarioType | "all")}
                />
              </Field>
              <Field title="Severity">
                <Select
                  style={{ width: "100%" }}
                  options={[
                    { key: "all", title: "All severities" },
                    { key: "high", title: "High" },
                    { key: "medium", title: "Medium" }
                  ]}
                  value={severityFilter}
                  onSelect={opt => setSeverityFilter(opt.key as "all" | "medium" | "high")}
                />
              </Field>
              <SearchInput value={search} onChange={setSearch} />
            </div>
          </Stack>
        </ControlsCard>

        {/* Enhanced Employee Table */}
        {filteredRows.length === 0 ? (
          <Empty title="No flagged employees" icon="personCheck" />
        ) : (
          <EnhancedEmployeeTable
            rows={filteredRows}
            selectedWorkflowId={selectedWorkflowId}
            onLaunchWorkflow={launchWorkflow}
            launchingEmployeeId={launchingEmployeeId}
          />
        )}
      </Stack>
    </TopBarRoute>
  );
};
